/**
 * L'ÉTAT D'ACCÈS applicatif du slot actif (BBG-211 / E4-T4, RFC §13/§14/§16).
 *
 * ─── Rendre, jamais décider ──────────────────────────────────────────────────
 *
 * La décision d'éligibilité est une fonction PURE du moteur (BBG-223), rendue
 * par le BFF sous `/portail/applications`. Ce module TRANSPORTE : il rend
 * l'état tel que le serveur le donne — ni filtré, ni recoupé, ni « corrigé ».
 * Une entrée contradictoire (par exemple `REGISTERED` non lançable un jour de
 * maintenance) est rendue TELLE QUELLE : la retoucher ferait de ce paquet une
 * seconde autorité, et c'est exactement ce que le critère d'acceptation
 * interdit — « le runtime rend l'état tel que le serveur le donne ; il n'en
 * dérive aucune décision ».
 *
 * ─── Aucune éligibilité n'ouvre un droit (K-25) ──────────────────────────────
 *
 * Ce fichier ne contient AUCUNE ligne qui transforme `eligibility` en capacité
 * : pas de comparaison sur son contenu, pas de filtre, pas de champ dérivé.
 * Un `REGISTERED` forgé côté client n'ouvre RIEN — le lancement revérifie tout
 * au moteur, à l'émission ET à la consommation (404 uniforme). Ce que ce
 * module ne peut pas faire, une régression ne peut pas l'introduire par
 * mégarde sans passer par une réécriture délibérée — la porte statique relit
 * ce fichier (RT-03).
 *
 * ─── D'où vient chaque morceau ────────────────────────────────────────────────
 *
 *   - le SLOT désigné : `sessionStorage` de CET onglet (jamais le serveur) ;
 *   - l'ÉTAT : le corps relayé par le BFF, validé au bord — une valeur hors
 *     vocabulaire est une rupture de contrat amont (bruit, jamais un repli
 *     deviné), même posture fail-closed que la décision au moteur ;
 *   - `lancable` : le nom du champ EXISTANT du portail — le RFC l'appelle
 *     `launchable`, le serveur sert `lancable` et ce paquet épouse le servi,
 *     jamais l'inverse (pas de rupture de contrat pour une coquetterie).
 *
 * `environnement` est rendu par le moteur mais n'entre PAS dans le contrat
 * `ApplicationEntry` du RFC §13 : le runtime ne l'expose pas — une surface non
 * décidée n'est pas une surface.
 */
import { lireSlotActif } from './onglet.js';
import { ErreurPortail, lireCorps, refusDe } from './transport.js';
const ELIGIBILITES = new Set([
    'REGISTERED',
    'PENDING',
    'SCHEDULED',
    'SUSPENDED',
    'EXPIRED',
    'REVOKED',
    'NOT_REGISTERED',
]);
const ONBOARDINGS = new Set([
    'SELF_REGISTRATION',
    'REQUEST_ACCESS',
    'INVITATION_ONLY',
    'NONE',
]);
/** Une réponse qui ne tient pas le contrat est une anomalie AMONT — du bruit, jamais une valeur devinée. */
function contratRompu() {
    return new ErreurPortail(0, 'contrat_amont', 'La réponse du portail ne tient pas le contrat annoncé.');
}
/** Sans sélection dans CET onglet, « à quoi ai-je accès ? » n'a pas de sujet — refus LOCAL, sans aller-retour. */
function slotRequis(stockage) {
    const slotRef = lireSlotActif(stockage);
    if (slotRef === null) {
        throw new ErreurPortail(0, 'aucune_selection', 'Choisissez d’abord un compte dans cet onglet.');
    }
    return slotRef;
}
function estObjet(valeur) {
    return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}
/**
 * Lit la liste rendue par le BFF. Chaque entrée est vérifiée CHAMP PAR CHAMP :
 * une entrée déformée n'est pas « rendue à moitié » ni écartée en silence
 * (elle ferait DISPARAÎTRE un accès réel de l'écran) — elle fait échouer la
 * lecture entière, en bruit.
 */
function lireEntrees(corps) {
    if (!estObjet(corps) || !Array.isArray(corps['applications']))
        throw contratRompu();
    return corps['applications'].map((brute) => {
        if (!estObjet(brute))
            throw contratRompu();
        const { clientId, name, iconUrl, statut, lancable, eligibility, onboarding, urls } = brute;
        if (typeof clientId !== 'string' || clientId === '')
            throw contratRompu();
        if (typeof name !== 'string')
            throw contratRompu();
        if (iconUrl !== null && typeof iconUrl !== 'string')
            throw contratRompu();
        if (typeof statut !== 'string' || statut === '')
            throw contratRompu();
        if (typeof lancable !== 'boolean')
            throw contratRompu();
        if (typeof eligibility !== 'string' || !ELIGIBILITES.has(eligibility))
            throw contratRompu();
        if (typeof onboarding !== 'string' || !ONBOARDINGS.has(onboarding))
            throw contratRompu();
        if (!Array.isArray(urls) || urls.some((u) => typeof u !== 'string'))
            throw contratRompu();
        return {
            clientId,
            name,
            iconUrl: iconUrl,
            statut,
            lancable,
            eligibility: eligibility,
            onboarding: onboarding,
            urls: urls,
        };
    });
}
/**
 * Le portail applicatif du slot actif de CET onglet.
 *
 * Le signal est celui de la génération courante : c'est `creerRuntime` qui
 * l'injecte et qui JUGE la résolution — une réponse née avant un switch ne
 * vaut plus, quand bien même elle serait intacte.
 *
 * Les refus du BFF se PROPAGENT intacts (`ErreurPortail` telle quelle) : le
 * consommateur doit pouvoir distinguer « ce compte n'est plus dans ce
 * navigateur » d'une panne — ce n'est pas à ce module de les réécrire.
 */
export async function listerApplications(transport, stockage, signal) {
    const reponse = await transport.appel('GET', '/portail/applications', {
        signal,
        slotRef: slotRequis(stockage),
    });
    const corps = await lireCorps(reponse);
    if (!reponse.ok)
        throw refusDe(reponse, corps);
    return lireEntrees(corps);
}
/**
 * « Rejoindre » (politique libre) ou « Demander l'accès » (politique demande)
 * au nom du slot actif — rend l'état APRÈS le geste, tel que le serveur l'a
 * constitué.
 *
 * Le `404` uniforme (application non offrable), le `409 deja_rattache`, le
 * `429` anti-spam et le `200` d'une demande DÉJÀ posée (idempotence du moteur,
 * relayée) sont des réponses : ils se propagent ou se rendent, jamais
 * réinterprétés.
 */
export async function demanderAcces(transport, stockage, clientId) {
    const reponse = await transport.appel('POST', `/portail/applications/${encodeURIComponent(clientId)}/acces`, { slotRef: slotRequis(stockage) });
    const corps = await lireCorps(reponse);
    if (!reponse.ok)
        throw refusDe(reponse, corps);
    if (!estObjet(corps))
        throw contratRompu();
    const eligibility = corps['eligibility'];
    if (typeof eligibility !== 'string' || !ELIGIBILITES.has(eligibility))
        throw contratRompu();
    return eligibility;
}
//# sourceMappingURL=applications.js.map